import { useEffect, useMemo, type RefObject } from 'react'
import {
  buildFormFieldFromMeta,
  normaliseRegistrationField,
  useForm,
  type FormContext,
} from '@/shared/lib/forms'
import irisApi from '../lib/api'
import type { UploadSession } from '../types'

export function IrisEntityForm({
  token,
  session,
  formRef,
  onUploadsChange,
}: {
  token: string
  session: UploadSession
  formRef: RefObject<FormContext | null>
  /** +1 when a photo upload starts, -1 when it settles. */
  onUploadsChange?: (delta: number) => void
}) {
  const fields = useMemo(() => {
    const fieldsMeta = session.form_fields ?? []
    const values = session.form_values ?? {}
    return fieldsMeta.map((raw) => {
      const meta = normaliseRegistrationField({
        uuid: raw.uuid,
        code_name: raw.code_name,
        display_name: raw.display_name,
        field_type: raw.field_type,
        required: raw.required,
        hint: raw.hint,
        description: raw.description,
        options: raw.options,
        validation_rules: raw.validation_rules as never,
        configuration_data: raw.configuration_data,
        order: raw.order,
      })
      return buildFormFieldFromMeta(meta, values[raw.code_name], 'submit')
    })
  }, [session.form_fields, session.form_values])

  const { Form, ref } = useForm(
    {
      name: 'iris-entity-form',
      submit: () => <></>,
      fields,
      uploadFile: async ({ file, fieldName }) => {
        const form = new FormData()
        form.append('file', file)
        if (fieldName) form.append('field_code', fieldName)
        onUploadsChange?.(1)
        try {
          const res = await irisApi.post(`/${token}/form-files/`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          const stored = res.data as {
            uuid?: string
            original_filename?: string
            url?: string
          }
          return {
            uuid: stored.uuid,
            file_name: stored.original_filename,
            file_url: stored.url,
          }
        } finally {
          onUploadsChange?.(-1)
        }
      },
    },
    async () => undefined,
    [token, onUploadsChange],
  )

  useEffect(() => {
    formRef.current = ref.current
    return () => {
      formRef.current = null
    }
  }, [formRef, ref])

  if (fields.length === 0) return null

  return (
    <div className="mb-8" data-testid="iris-entity-form">
      <Form className="flex flex-col gap-3" />
    </div>
  )
}
